export type PolygonPointArray = string[] | number[][];

export type ColoredPolygon = { points: PolygonPointArray; color: string };

export type OutlinedFillPolygon = { points: PolygonPointArray; outlinedFill: true };

export type PolygonEntry = PolygonPointArray | ColoredPolygon | OutlinedFillPolygon;

export interface AiDetectionResult {
  class: string;
  Condition?: string;
  Severity?: string;
  showForGender?: string;
  scalp_masks?: string;
  BoundingBoxes?: string[];
  PolygonPointsSequences?: PolygonEntry[];
  ellipse_points?: { left?: string[]; right?: string[] };
  hairline_points?: string[];
  ideal_hairline?: string[];
  ProblemName?: string;
  severity?: string;
  severity_ratio?: number | string;
  scalp_area_ratio?: number | string;
  hair_mask_area?: number | string;
  scalp_mask_area?: number | string;
  falling_hair?: boolean | null;
}

export const SCALP_SEGMENT_CLASS = 'scalp-segment';

export const HAIR_FRONT_DETECTION_CLASSES = ['temple-baldness', 'hairline'];

export enum HairFrontCardState {
  TempleBaldness = 'TEMPLE_BALDNESS',
  HairlineMapped = 'HAIRLINE_MAPPED',
  Captured = 'CAPTURED',
  None = 'NONE',
}

export interface HairDetectionRoute {
  isHairTopType: boolean;
  isHairFrontType: boolean;
  showNoScalpDetectionCard: boolean;
}

export const ScalpSeverity = {
  Normal: 'Normal',
  Mild: 'Mild',
  Moderate: 'Moderate',
  Severe: 'Severe',
  Unknown: 'Unknown',
} as const;

export type ScalpSeverityValue = typeof ScalpSeverity[keyof typeof ScalpSeverity];

export enum ScalpCardState {
  Detected = 'DETECTED',
  Normal = 'NORMAL',
  HairFrontDetected = 'HAIR_FRONT_DETECTED',
  NoDetection = 'NO_DETECTION',
  None = 'NONE',
}

export interface ScalpCardCopy {
  title: string;
  description?: string;
  reasonsHeading?: string;
  reasons?: string[];
}

export type ScalpCopyGender = 'Male' | 'Female';

export interface ScalpNoDetectionCopy {
  title: string;
  reasonsHeading: string;
  reasons: string[];
}

export interface ScalpParameterLabels {
  crownDensity: string;
  partitionWidth: string;
  scalpVisibility: string;
}

export interface ScalpSeverityLanguageCopy {
  titles: Record<ScalpSeverityValue, string>;
  parameterLabels: ScalpParameterLabels;
  descriptions: Record<ScalpCopyGender, Record<ScalpSeverityValue, string>>;
  noDetection: ScalpNoDetectionCopy;
  hairFrontDetected: { title: string; description: string };
}

export const CROWN_DENSITY_TARGET_RANGE = '80-100%';
export const PARTITION_WIDTH_TARGET_RANGE = '0-20%';
export const SCALP_VISIBILITY_TARGET_RANGE = '0-20%';

export interface ScalpSeverityMultipliers {
  crownDensityCoefficient: number;
  ratioInflation: number;
}

export const SCALP_SEVERITY_MULTIPLIERS: Record<ScalpSeverityValue, ScalpSeverityMultipliers> = {
  [ScalpSeverity.Normal]: { crownDensityCoefficient: 0.9, ratioInflation: 1.1 },
  [ScalpSeverity.Mild]: { crownDensityCoefficient: 0.8, ratioInflation: 1.2 },
  [ScalpSeverity.Moderate]: { crownDensityCoefficient: 0.7, ratioInflation: 1.3 },
  [ScalpSeverity.Unknown]: { crownDensityCoefficient: 0.7, ratioInflation: 1.3 },
  [ScalpSeverity.Severe]: { crownDensityCoefficient: 0.6, ratioInflation: 1.4 },
};
